<template>
  <div id="navigatorMap"></div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-edgebuffer'
import 'leaflet-contextmenu/dist/leaflet.contextmenu.min'
import 'leaflet-contextmenu/dist/leaflet.contextmenu.min.css'
import 'leaflet-ellipse/l.ellipse.min'

// Do not use Leaflet.Graticule from NPM Repo, instead use fixed local version
import '@/../node_modules_adapted/leaflet-graticule/Leaflet.Graticule'

import colormap from 'colormap'

import BelugaMapOverlays from '@/mixins/beluga_map_overlays'
import BelugaMapControls from '@/mixins/beluga_map_controls'
import BelugaMapRenderer from '@/mixins/beluga_map_renderer'
import BelugaMessageFactory from '@/mixins/beluga_messagefactory'
import Helpers from '@/mixins/helpers'
import { mapState, mapGetters, mapMutations } from 'vuex'
import $ from 'jquery'

const DEFAULT_BASE_LAYER = 'google_terrain'

// Leaflet Workarounds
// eslint-disable-next-line
delete L.Icon.Default.prototype._getIconUrl
// eslint-disable-next-line
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// Add some more control positioning options
L.Map.include({
  _initControlPos: function () {
    const corners = this._controlCorners = {}
    const l = 'leaflet-'
    const container = this._controlContainer = L.DomUtil.create('div', l + 'control-container', this._container)

    function createCorner (vSide, hSide) {
      const className = l + vSide + ' ' + l + hSide

      corners[vSide + hSide] = L.DomUtil.create('div', className, container)
    }

    createCorner('top', 'left')
    createCorner('top', 'right')
    createCorner('bottom', 'left')
    createCorner('bottom', 'right')

    createCorner('top', 'center')
    createCorner('middle', 'center')
    createCorner('middle', 'left')
    createCorner('middle', 'right')
    createCorner('bottom', 'center')
  }
})

export default {
  name: 'MapViewWidget',
  data () {
    return {
      map: null,
      options: {
        lockMap: false,
        minZoom: 3,
        maxZoom: 25,
        maxNativeZoom: 18,
        zoomSnap: 1,
        zoomDelta: 1,
        wheelPxPerZoomLevel: 200,
        initialZoom: 9,
        initialCentered: false,
        selectionZoom: 18,
        flytoZoom: 13,
        fallbackLocation: [54.183929, 9.824524], // Kiel: 54.329038, 10.176336
        tileSize: 256,
        colormapShades: 40
      },
      layers: {},
      tools: {
        rangingBearing: {
          active: false,
          ranges: {},
          markers: {},
          selectedPlatform: 0
        }
      },
      controls: {
        coordinatesView: null,
        parameterView: null,
        rangingBearing: null,
        lockMap: null
      },
      cache: {
        dataMarkers: {}
      },
      colormaps: {}
    }
  },
  mixins: [
    BelugaMapOverlays,
    BelugaMapControls,
    BelugaMapRenderer,
    BelugaMessageFactory,
    Helpers
  ],
  created () {
    const widget = this
    // Subscribe to store mutations
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'add_campaign') {
        // Add bounds field to new campaign
        // if (!('bounds' in this.campaign(mutation.payload.id))) {
        //  this.campaign(mutation.payload.id).bounds = {}
        // }
      } else if (mutation.type === 'add_media_feed') {
        this.addMediaFeed(widget, Number(mutation.payload.campaignId))
      } else if (mutation.type === 'select_campaign') {
        // Deselect all platforms first
        this.deselectPlatforms(widget)
        // Add all campaign platforms to map, remove all others
        // Make sure that platform markers are properly rescaled depending on zoom level
        for (const platformId in this.platforms) {
          if (Object.prototype.hasOwnProperty.call(this.platforms, platformId)) {
            this.handlePlatformVisibility(widget, platformId)
            this.handlePlatformMarkers(widget, platformId)
          }
        }
        // Deactivate ranging/bearing tool
        widget.tools.rangingBearing.active = false
        widget.controls.rangingBearing.state('ranging-disabled')
        // Reset map lock tool
        widget.options.lockMap = true
        widget.controls.lockMap.state('lock-enabled')
        // Reset map center
        widget.options.initialCentered = false
        // Load campaign zoom
        const zoom = this.campaign(mutation.payload).option_zoom
        if (zoom > 0) {
          widget.map.setZoom(zoom)
        }
        // Handle overlays
        if (this.previousCampaign > 0) {
          // Remove previous base layer if available
          const oldBaselayer = this.campaign(this.previousCampaign).option_baselayer
          if (oldBaselayer !== '') {
            widget.map.removeLayer(widget.layers[oldBaselayer])
          }
          // Remove media feed items if available
          this.removeMediaFeed(widget, this.previousCampaign)
        }
        // Add new base layer
        // Load campaign base layer
        let baselayer = this.campaign(mutation.payload).option_baselayer
        if (baselayer === '') {
          baselayer = DEFAULT_BASE_LAYER
        }
        widget.map.addLayer(widget.layers[baselayer])
        // Render media feed overlay of campaign
        this.renderMediaFeed(widget, this.selectedCampaign)
        // FIXME: Hard coded special treatment of various campaigns -> Dynamic!!
        if (widget.map.hasLayer(widget.layers.bsh_bathymetry)) {
          widget.map.removeLayer(widget.layers.bsh_bathymetry)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_science)) {
          widget.map.removeLayer(widget.layers.ocean_change_science)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_stations2024)) {
          widget.map.removeLayer(widget.layers.ocean_change_stations2024)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_ctdcasts2024)) {
          widget.map.removeLayer(widget.layers.ocean_change_ctdcasts2024)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_eyeonwater2024)) {
          widget.map.removeLayer(widget.layers.ocean_change_eyeonwater2024)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_stations2025)) {
          widget.map.removeLayer(widget.layers.ocean_change_stations2025)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_eyeonwater2025)) {
          widget.map.removeLayer(widget.layers.ocean_change_eyeonwater2025)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_eyeonwater2026)) {
          widget.map.removeLayer(widget.layers.ocean_change_eyeonwater2026)
        }
        if (widget.map.hasLayer(widget.layers.wind_1p00_particles)) {
          widget.map.removeLayer(widget.layers.wind_1p00_particles)
        }
        if (widget.map.hasLayer(widget.layers.dwd_wolken)) {
          widget.map.removeLayer(widget.layers.dwd_wolken)
        }
        if (widget.map.hasLayer(widget.layers.day_night)) {
          widget.map.removeLayer(widget.layers.day_night)
        }
        if (widget.map.hasLayer(widget.layers.s4o2)) {
          widget.map.removeLayer(widget.layers.s4o2)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_ctdcasts2024)) {
          widget.map.removeLayer(widget.layers.ocean_change_ctdcasts2024)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_ctdcasts2025)) {
          widget.map.removeLayer(widget.layers.ocean_change_ctdcasts2025)
        }
        if (widget.map.hasLayer(widget.layers.ocean_change_ctdcasts2026)) {
          widget.map.removeLayer(widget.layers.ocean_change_ctdcasts2026)
        }
        if (this.selectedCampaign === 1 || this.selectedCampaign === 15 || this.selectedCampaign === 17) {
          if (this.selectedCampaign === 1 || this.selectedCampaign === 17) {
            widget.map.addLayer(widget.layers.ocean_change_ctdcasts2024)
            widget.map.addLayer(widget.layers.ocean_change_science)
          }
          if (this.selectedCampaign === 15) {
            widget.map.addLayer(widget.layers.dwd_wolken)
            widget.map.addLayer(widget.layers.day_night)
            // FIXME: Load wind after data was downloaded
            widget.map.addLayer(widget.layers.wind_1p00_particles)
          }
        }
        if (this.selectedCampaign === 19) {
          widget.map.addLayer(widget.layers.s4o2)
        }
        if (this.selectedCampaign === 26) { // Ocean Change 2024
          widget.map.addLayer(widget.layers.ocean_change_stations2024)
          widget.map.addLayer(widget.layers.ocean_change_ctdcasts2024)
          widget.map.addLayer(widget.layers.ocean_change_eyeonwater2024)
        }
        if (this.selectedCampaign === 29) { // Ocean Change 2025
          widget.map.addLayer(widget.layers.ocean_change_stations2025)
          widget.map.addLayer(widget.layers.ocean_change_eyeonwater2025)
          widget.map.addLayer(widget.layers.ocean_change_ctdcasts2025)
        }
        if (this.selectedCampaign === 32) { // Ocean Change 2026
          widget.map.addLayer(widget.layers.ocean_change_eyeonwater2026)
          widget.map.addLayer(widget.layers.ocean_change_ctdcasts2026)
        }
        // if (this.selectedCampaign === 20) {
        //   widget.map.addLayer(widget.layers.bsh_bathymetry)
        // }
        // Handle controls
        // FIXME: Dynamic!
        if (widget.controls.coordinatesView._map !== undefined) {
          widget.map.removeControl(widget.controls.coordinatesView)
        }
        // if ('commands' in this.$route.query || this.selectedCampaign === 15) {
        widget.map.addControl(widget.controls.coordinatesView)
        // }
      } else if (mutation.type === 'add_platform') {
        this.addPlatform(widget, Number(mutation.payload.id))
      } else if (mutation.type === 'update_parameters_ros') {
        this.renderPlatform(widget, Number(mutation.payload.platform_id))
      } else if (mutation.type === 'update_parameters_rest') {
        // Only render platforms if there are actually new values
        if (Object.keys(mutation.payload.values).length > 0) {
          this.renderPlatform(widget, Number(mutation.payload.platform_id))
        }
      } else if (mutation.type === 'select_platform') {
        // Select platform and center map on it
        if (this.selectedPlatform > 0) {
          this.selectPlatform(widget, this.selectedPlatform,
            !widget.options.initialCentered || widget.options.lockMap)
        } else {
          // Do nothing, deselecting all platforms is already done by map onclick handler
          this.deselectPlatforms(widget)
        }
      } else if (mutation.type === 'select_parameter') {
        // Clear data marker
        this.renderDataMarker(widget, null)
        // Clear data markers cache
        this.cache.dataMarkers = {}
        // Colorize track
        this.colorizeTrack(widget, mutation.payload)
      } else if (mutation.type === 'select_feed_item') {
        if (mutation.payload !== null) {
          // Disable map locking to allow flying to media feed item
          widget.options.lockMap = false
          widget.controls.lockMap.state('lock-disabled')
          this.centerFeedItem(widget, mutation.payload)
        }
      } else if (mutation.type === 'set_collapse' || mutation.type === 'toggle_collapse') {
        this.refreshViewport()
      } else if (mutation.type === 'select_timestamp') {
        // Only render data marker on track if nav track is available
        if (this.selectedPlatform > 0 && 'nav' in this.platformSelected.map.tracks) {
          // Get the closest track point
          if (!(mutation.payload.timestamp in this.cache.dataMarkers)) {
            // Retrieve the closest track point and cache location and data
            this.cache.dataMarkers[mutation.payload.timestamp] = widget.getClosestValueObject(mutation.payload.timestamp, this.platformSelected.cache.tracks.nav)
            this.cache.dataMarkers[mutation.payload.timestamp].timestamp = mutation.payload.timestamp
            this.cache.dataMarkers[mutation.payload.timestamp].data = mutation.payload.data
            this.cache.dataMarkers[mutation.payload.timestamp].parameter = this.selectedParameter
          }
          widget.renderDataMarker(widget, this.cache.dataMarkers[mutation.payload.timestamp])
        }
      }
    })
  },
  mounted () {
    // Initialize map
    this.$nextTick(() => {
      this.initMap()
    })
  },
  beforeDestroy () {
    // Unsubscribe from store mutations
    this.unsubscribe()
  },
  computed: {
    ...mapState(['selectedPlatform', 'selectedCampaign', 'selectedParameter', 'previousCampaign']),
    ...mapGetters(['platforms', 'platformSelected', 'campaignSelected', 'campaign', 'campaigns', 'platform', 'entity', 'parameter', 'mediaFeed'])
  },
  methods: {
    ...mapMutations(['select_platform', 'select_feed_item', 'select_parameter', 'select_async_dataset']),
    // zoomToCampaign: function (campaignId) {
    //   // If no platform is selected, zoom to campaign
    //   console.log('map widget: zooming in to campaign ' + campaignId)
    //   // Fly to bounds
    //   if (Object.keys(this.campaign(campaignId).bounds).length > 0) {
    //     this.map.fitBounds(Object.values(this.campaign(campaignId).bounds), {
    //       maxZoom: 17 // FIXME: Magic number?
    //     })
    //   }
    // },
    getColormap: function (cm = 'jet', alpha = 1.0) {
      // TODO: Allow inverse colormaps
      if (!(cm in this.colormaps)) {
        try {
          // Create colormap
          console.debug('map: creating new color map "' + cm + '"')
          this.colormaps[cm] = colormap({
            colormap: cm,
            nshades: this.options.colormapShades,
            format: 'hex',
            alpha: alpha
          })
        } catch (e) {
          console.warn('map: could not find color map "' + cm + '", switching to "jet"')
          // Set default colormap
          cm = 'jet'
        }
      }
      return this.colormaps[cm]
    },
    refreshViewport: function () {
      if (this.map !== null) {
        this.map.invalidateSize()
      }
    },
    initMap: function () {
      const widget = this

      // Init base layers
      this.initLayers(widget)

      // Create map
      this.map = L.map('navigatorMap', {
        layers: [
          this.layers.openseamap,
          this.layers.geographic_lines,
          this.layers.arcgis_gebco_bathymetric_gridlines
        ],
        zoom: this.options.initialZoom,
        zoomDelta: this.options.zoomDelta,
        minZoom: this.options.minZoom,
        maxZoom: this.options.maxZoom,
        zoomSnap: this.options.zoomSnap,
        zoomAnimation: false,
        wheelPxPerZoomLevel: this.options.wheelPxPerZoomLevel,
        center: this.options.fallbackLocation,
        contextmenu: true,
        contextmenuWidth: 180,
        maxBounds: new L.LatLngBounds([-90, -180], [90, 180]),
        worldCopyJump: false,
        preferCanvas: false,
        contextmenuItems: [{
          text: 'Show coordinates',
          callback: (e) => {
            const coords = this.getCoordinatesNautical(e.latlng.lat, e.latlng.lng)
            alert('Nautical: ' + coords.lat + ', ' + coords.lng + '; Decimal: ' +
            this.roundDigits(e.latlng.lat, 6) + ', ' + this.roundDigits(e.latlng.lng, 6))
          }
        }, {
          text: 'Center map here',
          callback: (e) => {
            this.map.panTo(e.latlng)
          }
        }, '-', {
          type: 'auv-g500',
          text: 'AUV: Go to position',
          callback: (event) => {
            if (this.selectedPlatform !== 0 && this.platformSelected.type === 'auv-g500') {
              this.sendGoto(widget, this.selectedPlatform, event.latlng.lat, event.latlng.lng)
            } else {
              alert('Selected platform is no AUV!')
            }
          }
        }, '-', {
          type: 'transponder',
          text: 'Transponder: Send position',
          callback: (event) => {
            if (this.selectedPlatform !== 0 && this.platformSelected.type === 'transponder') {
              this.updateTransponderPosition(widget, this.selectedPlatform, event.latlng.lat, event.latlng.lng)
            } else {
              alert('Selected platform is no transponder!')
            }
          }
        }]
      })

      // Map event handlers
      this.map.on('click', (event) => {
        if (event.originalEvent.shiftKey) {
          if (this.selectedPlatform !== 0 && this.platformSelected.type === 'auv-g500') {
            this.sendGoto(widget, this.selectedPlatform, event.latlng.lat, event.latlng.lng)
          } else {
            alert('Selected platform is no AUV!')
          }
        } else if (event.originalEvent.ctrlKey) {
          if (this.selectedPlatform !== 0 && this.platformSelected.type === 'auv-g500') {
            this.sendGotoSurface(widget, this.selectedPlatform, event.latlng.lat, event.latlng.lng)
          }
        } else {
          this.deselectPlatforms(widget)
        }
        console.debug('map view: current zoom level: ' + widget.map.getZoom())
      })
      this.map.on('layeradd', (event) => {
        if (event.layer.options.name === 'test') {
          // event.layer.options.renderer.setDisplayRange(event.layer.getMinMax())
        }
      })
      this.map.on('zoomend', () => {
        for (const platformId in this.platforms) {
          if (Object.prototype.hasOwnProperty.call(this.platforms, platformId)) {
            this.handlePlatformMarkers(widget, platformId)
          }
        }
      })
      this.map.on('viewreset', () => {
        widget.map.invalidateSize()
      })
      this.map.on('mousemove', (event) => {
        const coordsNautical = widget.getCoordinatesNautical(event.latlng.lat, event.latlng.lng)
        $('#coords-nautical').html(coordsNautical.lat + ', ' + coordsNautical.lng)
        $('#coords-decimal').html('Decimal: ' + widget.showDigits(event.latlng.lat, 5) + ', ' + widget.showDigits(event.latlng.lng, 5))
      })

      L.latlngGraticule({
        showLabel: true,
        color: '#000000',
        opacity: 0.4,
        weight: 0.75,
        zoomInterval: [
          { start: 2, end: 3, interval: 20 },
          { start: 2, end: 5, interval: 10 },
          { start: 6, end: 6, interval: 5 },
          { start: 7, end: 8, interval: 1 },
          { start: 8, end: 9, interval: 0.5 },
          { start: 10, end: 10, interval: 0.25 },
          { start: 11, end: 12, interval: 0.1 },
          { start: 12, end: 13, interval: 0.05 },
          { start: 13, end: 14, interval: 0.025 },
          { start: 15, end: 20, interval: 0.01 }
        ],
        format: 'dd',
        precision: 2
      }).addTo(this.map)

      // Init controls
      this.initControls(widget)
      // Invalidate map size
      this.refreshViewport()
    }
  }
}
</script>

<style lang="scss">

// TODO: Scoped?

.leaflet-bar a, .leaflet-bar a:hover {
  text-decoration: none !important;
}

#navigatorMap {
  height: 100%;
  background-color: rgba(0,0,0,0.03); /*#9cc0fa*/
}

.leaflet-control-layers-base > label {
  margin-bottom: 0;
}

.leaflet-control-layers-group > label {
  margin-bottom: 0;
}

.leaflet-control-layers-group-name {
  margin-left: 0;
  margin-bottom: 2px;
  font-weight: bold;
  font-size: 13px;
}

.total-popup-content {
  width: 80px;
}

.transparent-tooltip {
  background-color: rgb(255, 255, 255, 1);
  border-radius: 2px;
  padding: 0 4px 0 4px;
  border: 0;
  box-shadow: none;
  font-size: 11px
}

.solid-tooltip {
  background-color: rgba(0, 0, 0, 1);
  border-radius: 2px;
  padding: 0 4px 0 4px;
  border: 0;
  box-shadow: none;
  font-size: 11px;
  color: #ffffff;
}

.ctdcast-tooltip {
  background-color: rgba(60, 216, 230, 1.0);
  border-radius: 2px;
  padding: 0 5px 0 5px;
  border: 0;
  box-shadow: none;
  font-size: 12px;
  color: #ffffff;
}

.station-tooltip {
  background-color: rgba(207, 23, 15, 1.0);
  border-radius: 2px;
  padding: 0 5px 0 5px;
  border: 0;
  box-shadow: none;
  font-size: 12px;
  color: #ffffff;
}

.station-tooltip-inactive {
  background-color: #979797;
  border-radius: 2px;
  padding: 0 5px 0 5px;
  border: 0;
  box-shadow: none;
  font-size: 13px;
  color: #000000;
}

.info-tooltip {
  background-color: rgba(0, 123, 255, 1.0);
  border-radius: 2px;
  padding: 0 5px 0 5px;
  border: 0;
  box-shadow: none;
  font-size: 12px;
  color: #ffffff;
}

.eyeonwater-tooltip {
  background-color: rgba(222, 241, 241, 1.0);
  border-radius: 2px;
  padding: 0 5px 0 5px;
  border: 0;
  box-shadow: none;
  font-size: 12px;
  color: #3b3567;
}

.feed-tooltip {
  background-color: rgba(255, 255, 255, 1.0);
  border-radius: 2px;
  padding: 0 5px 0 5px;
  border: 0;
  box-shadow: none;
  font-size: 12px;
  color: #000000;
}

.geometry-line-tooltip {
  background-color: rgba(255, 255, 255, 1.0);
  border-radius: 2px;
  padding: 0 5px 0 5px;
  border: 0;
  box-shadow: none;
  font-size: 12px;
  color: #000000;
}

.transparent-tooltip::before {
  border: none;
}

.s4o-tooltip {
  background-color: rgba(26, 26, 255, 1.0);
  border-radius: 2px;
  padding: 0 5px 0 5px;
  border: 0;
  box-shadow: none;
  font-size: 12px;
  color: #ffffff;
}

.selection-icon {
  border: 0;
  background: transparent;
}

.control-icon {
  width: 13px;
  height: 13px;
  vertical-align: text-top;
}

.leaflet-center {
  left: 50%;
  transform: translate(-50%, 0%);
}

.leaflet-middle {
  top: 50%;
  position: absolute;
  z-index: 1000;
  pointer-events: none;
  transform: translate(0%, -50%);
}

.leaflet-center.leaflet-middle {
  transform: translate(-50%, -50%);
}

.leaflet-control-coordsview {
  z-index: 0;
}

.leaflet-control-coordsview-control {
  width: 190px;
  background-color: #ffffff;
  padding: 5px 10px 4px 10px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  -webkit-background-clip: padding-box; /* for Safari */
  background-clip: padding-box; /* for IE9+, Firefox 4+, Opera, Chrome */
  border-radius: 4px;
}

.leaflet-control-environparams {
  width: 275px;
  left: -135px;
  background-color: #ffffff;
  padding: 5px 10px 2px 10px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  -webkit-background-clip: padding-box; /* for Safari */
  background-clip: padding-box; /* for IE9+, Firefox 4+, Opera, Chrome */
  border-radius: 4px;
}

.leaflet-control-environparams-param {
  font-size: 13px;
  font-weight: bold;
  line-height: 15px;
}

.leaflet-control-coordsview-nautical {
  font-size: 12px;
  font-weight: bold;
  display: block;
}

.leaflet-control-coordsview-decimal {
  font-size: 11px;
  display: block;
}

.leaflet-control-paramview-control {
  background-color: #ffffff;
  padding: 5px 10px 5px 10px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  -webkit-background-clip: padding-box; /* for Safari */
  background-clip: padding-box; /* for IE9+, Firefox 4+, Opera, Chrome */
  border-radius: 4px;
}

.leaflet-control-paramview-parameter {
  font-size: 13px;
  display: block;
  margin-right: 10px;
}

.leaflet-control-paramview-colormap {
  display: inline;
}

.leaflet-control-paramview-shade {
  display: inline-block;
  margin: 0;
  padding: 0;
  height: 9px;
  width: 4px;
}

.leaflet-control-paramview-min {
  font-size: 13px;
  padding-right: 8px;
  display: inline;
}

.leaflet-control-paramview-max {
  font-size: 13px;
  padding-left: 8px;
  display: inline;
}

</style>
